import React, { useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";

// icons
import { Boxes, Loader, Search, Truck, PackageCheck, PackageX, RefreshCcw } from 'lucide-react';
import { Link } from "react-router-dom";
import TableContainer from "Common/TableContainer";

// react-redux
import { useDispatch, useSelector } from 'react-redux';

import { getShipment } from 'slices/thunk';
import { RootState } from 'slices';
import { ToastContainer } from "react-toastify";

const Orders = () => {
    const dispatch = useDispatch();
    const shipment = useSelector((state: RootState) => state.Ecommerce.shipment);

    // Separate state for original data and filtered data
    const [originalData, setOriginalData] = useState<any>([]);
    const [filteredData, setFilteredData] = useState<any>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("1");

    useEffect(() => {
        dispatch(getShipment() as any);
    }, [dispatch]);

    useEffect(() => {
        if (shipment && Array.isArray(shipment)) {
            const mapped = shipment.map((item: any, index: number) => ({
                id: index + 1,
                receiverName: item.receiverName,
                shipperName: item.shipperName,
                orderDate: new Date(item.delivery).toLocaleDateString(),
                transferCharge: `${item.transferCharge}`,
                quantity: item.Quantity,
                currentLocation: item.currentLocation,
                destination: item.destination,
                totalAmount: item.totalvalue,
                deliveryStatus: item.status.charAt(0).toUpperCase() + item.status.slice(1),
            }));
            setOriginalData(mapped);
            setFilteredData(mapped); // Initialize filtered data
        }
    }, [shipment]);

    // Apply both search and tab filters
    const applyFilters = (data: any[], search: string, tabType: string) => {
        let filtered = [...data];

        // Apply tab filter first
        if (tabType !== "all") {
            filtered = filtered.filter((order: any) =>
                order.deliveryStatus.toLowerCase() === tabType.toLowerCase()
            );
        }

        // Apply search filter
        if (search.trim()) {
            const keysToSearch = ['id', 'receiverName', 'shipperName', 'deliveryStatus'];
            filtered = filtered.filter((item: any) =>
                keysToSearch.some(key =>
                    item[key]?.toString().toLowerCase().includes(search.toLowerCase())
                )
            );
        }

        return filtered;
    };

    // Search Data
    const filterSearchData = (e: any) => {
        const search = e.target.value;
        setSearchTerm(search);

        // Get current tab type
        const tabTypeMap: any = {
            "1": "all",
            "2": "Pending",
            "3": "Recieved", // Note: keeping the typo to match your data
            "5": "Cancelled"
        };

        const currentTabType = tabTypeMap[activeTab] || "all";
        const filtered = applyFilters(originalData, search, currentTabType);
        setFilteredData(filtered);
    };

    const toggleTab = (tab: any, type: any) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            const filtered = applyFilters(originalData, searchTerm, type);
            setFilteredData(filtered);
        }
    };

    // Status component
    const Status = ({ item }: any) => {
        switch (item) {
            case "Recieved":
                return (<span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-green-100 border-green-200 text-green-500 dark:bg-green-500/20 dark:border-green-500/20">{item}</span>);
            case "Pending":
                return (<span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-yellow-100 border-yellow-200 text-yellow-500 dark:bg-yellow-500/20 dark:border-yellow-500/20">{item}</span>);
            case "Cancelled":
                return (<span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-red-100 border-red-200 text-red-500 dark:bg-red-500/20 dark:border-red-500/20">{item}</span>);
            default:
                return (<span className="delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border bg-green-100 border-green-200 text-green-500 dark:bg-green-500/20 dark:border-green-500/20">{item}</span>);
        }
    };

    const columns = useMemo(() => [
        {
            header: "#",
            accessorKey: "id",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Delivery Date",
            accessorKey: "orderDate",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Shipper Name",
            accessorKey: "shipperName",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Receiving Manager",
            accessorKey: "receiverName",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Volumn",
            accessorKey: "transferCharge",
            enableColumnFilter: false,
            enableSorting: true,
        },

        {
            header: "Transfer Charge",
            accessorKey: "transferCharge",
            enableColumnFilter: false,
            enableSorting: true,
        },

        {
            header: "Total Amount",
            accessorKey: "totalAmount",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Export Location",
            accessorKey: "currentLocation",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Destination",
            accessorKey: "destination",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Delivery Status",
            accessorKey: "deliveryStatus",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cell: any) => (
                <Status item={cell.getValue()} />
            ),
        },
    ], []);

    return (
        <React.Fragment>
            <BreadCrumb title='Shipping Agent Module' pageTitle='Ecommerce' />
            <ToastContainer closeButton={false} limit={1} />

            <div className="card" id="ordersTable">
                <div className="card-body">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                        <div className="lg:col-span-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    placeholder="Search for ..."
                                    autoComplete="off"
                                    value={searchTerm}
                                    onChange={(e) => filterSearchData(e)}
                                />
                                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                            </div>
                        </div>
                    </div>

                    <ul className="flex flex-wrap w-full mt-5 text-sm font-medium text-center text-gray-500 nav-tabs">
                        <li className={`group ${activeTab === "1" && "active"}`}>
                            <Link to="#" data-tab-toggle data-target="allOrders" className="inline-block px-4 py-1.5 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent group-[.active]:bg-custom-500 group-[.active]:text-white dark:group-[.active]:text-white hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px]" onClick={() => { toggleTab("1", "all"); }}>
                                <Boxes className="inline-block size-4 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">All Orders</span>
                            </Link>
                        </li>
                        <li className={`group ${activeTab === "2" && "active"}`}>
                            <Link to="#" data-tab-toggle data-target="pendingOrder" className="inline-block px-4 py-1.5 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent group-[.active]:bg-custom-500 group-[.active]:text-white dark:group-[.active]:text-white hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px]" onClick={() => { toggleTab("2", "Pending"); }}>
                                <Loader className="inline-block size-4 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Pending</span>
                            </Link>
                        </li>
                        <li className={`group ${activeTab === "3" && "active"}`}>
                            <Link to="#" data-tab-toggle data-target="deliveredOrder" className="inline-block px-4 py-1.5 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent group-[.active]:bg-custom-500 group-[.active]:text-white dark:group-[.active]:text-white hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px]" onClick={() => { toggleTab("3", "Recieved"); }}>
                                <PackageCheck className="inline-block size-4 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Recieved</span>
                            </Link>
                        </li>
                        <li className={`group ${activeTab === "5" && "active"}`}>
                            <Link to="#" data-tab-toggle data-target="cancelledOrders" className="inline-block px-4 py-1.5 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent group-[.active]:bg-custom-500 group-[.active]:text-white dark:group-[.active]:text-white hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px]" onClick={() => { toggleTab("5", "Cancelled"); }}>
                                <PackageX className="inline-block size-4 ltr:mr-1 rtl:ml-1 " /> <span className="align-middle">Cancelled</span>
                            </Link>
                        </li>
                    </ul>

                    {filteredData && filteredData.length > 0 ?
                        <TableContainer
                            isPagination={true}
                            columns={(columns || [])}
                            data={(filteredData || [])}
                            customPageSize={10}
                            divclassName="mt-5 overflow-x-auto"
                            tableclassName="w-full whitespace-nowrap"
                            theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                            thclassName="px-3.5 py-2.5 font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
                            tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                            PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                        />
                        :
                        (<div className="noresult">
                            <div className="py-6 text-center">
                                <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                                <p className="mb-0 text-slate-500 dark:text-zink-200">We've searched more than 299+ orders We did not find any orders for you search.</p>
                            </div>
                        </div>)}
                </div>
            </div>
        </React.Fragment>
    );
};

export default Orders;